/**
 * Jadle keyword/modifier translation for locale-aware editing.
 * The stored Jadle is always English. CodeMirror translates keywords
 * on the way in (English → locale) and on the way out (locale → English).
 * @module jadleTranslate
 */

import { getLocale } from "./locale";

// ── Keyword translations (line-start keywords) ─────────────────────

type TranslationMap = Record<string, string>;

const keywordTranslations: Record<string, TranslationMap> = {
	fr: {
		title: "titre",
		subtitle: "sous-titre",
		supertitle: "supertitre",
		image: "image",
		background: "fond",
		video: "vidéo",
		audio: "audio",
		map: "carte",
		box: "boîte",
		line: "ligne",
		button: "bouton",
		columnTitle: "titreColonne",
		paragraph: "paragraphe",
		read: "lire",
		blank: "vide",
		pause: "pause",
		filter: "filtre",
		storyName: "nomHistoire",
		autospace: "autoespace",
		autoplay: "autolecture",
		moveBy: "naviguerPar",
		form: "formulaire",
		chart: "graphique",
	},
};

// ── Modifier translations (indented lines) ──────────────────────────

const modifierTranslations: Record<string, TranslationMap> = {
	fr: {
		align: "aligner",
		across: "travers",
		anchor: "ancre",
		animate: "animer",
		animateStart: "débutAnimation",
		background: "fond",
		backlink: "lienRetour",
		blur: "flou",
		"border-color": "couleur-bordure",
		"border-thickness": "épaisseur-bordure",
		caption: "légende",
		"chart-type": "type-graphique",
		color: "couleur",
		columns: "colonnes",
		"corner-radius": "rayon-coin",
		decoration: "décoration",
		delay: "délai",
		down: "bas",
		effect: "effet",
		font: "police",
		height: "hauteur",
		hideOn: "masquerSur",
		"line-height": "hauteur-ligne",
		loop: "boucle",
		mailto: "courriel",
		mute: "muet",
		opacity: "opacité",
		padding: "marge",
		"padding-bottom": "marge-bas",
		"padding-left": "marge-gauche",
		"padding-right": "marge-droite",
		"padding-top": "marge-haut",
		shadow: "ombre",
		size: "taille",
		style: "style",
		tap: "appui",
		toPage: "versPage",
		url: "url",
		weight: "graisse",
		width: "largeur",
		widescreen: "panoramique",
	},
};

// ── Cached reverse maps ─────────────────────────────────────────────

function reverseMap(map: TranslationMap): TranslationMap {
	const reversed: TranslationMap = {};
	for (const [en, loc] of Object.entries(map)) {
		reversed[loc] = en;
	}
	return reversed;
}

const reverseKeywordCache: Record<string, TranslationMap> = {};
const reverseModifierCache: Record<string, TranslationMap> = {};

function getReverseKeywords(locale: string): TranslationMap {
	if (!reverseKeywordCache[locale]) {
		const map = keywordTranslations[locale];
		reverseKeywordCache[locale] = map ? reverseMap(map) : {};
	}
	return reverseKeywordCache[locale];
}

function getReverseModifiers(locale: string): TranslationMap {
	if (!reverseModifierCache[locale]) {
		const map = modifierTranslations[locale];
		reverseModifierCache[locale] = map ? reverseMap(map) : {};
	}
	return reverseModifierCache[locale];
}

// ── Translation functions ───────────────────────────────────────────

/**
 * Translate a single line's keyword or modifier.
 * Returns the translated line, or the original if no match.
 */
function translateLine(
	line: string,
	keywordMap: TranslationMap,
	modifierMap: TranslationMap,
): string {
	// Skip empty lines
	if (!line) return line;

	// Check for indented modifier (2+ spaces then keyword:)
	const modMatch = line.match(/^(\s{2,})(\S+?):(.*)/);
	if (modMatch) {
		const [, indent, name, rest] = modMatch;
		if (name in modifierMap) {
			return `${indent}${modifierMap[name]}:${rest}`;
		}
		return line;
	}

	// Check for line-start keyword (no leading whitespace)
	const kwMatch = line.match(/^(\S+?):(.*)/);
	if (kwMatch) {
		const [, name, rest] = kwMatch;
		// Skip symbols that look like keywords but aren't (e.g. http:, https:)
		if (name in keywordMap) {
			return `${keywordMap[name]}:${rest}`;
		}
	}

	return line;
}

/**
 * Translate English Jadle to the user's locale for display in CodeMirror.
 * No-op for "en" or unknown locales.
 */
export function translateJadleToLocale(code: string, locale?: string): string {
	const loc = locale ?? getLocale();
	if (!loc || loc === "en") return code;
	const kwMap = keywordTranslations[loc];
	const modMap = modifierTranslations[loc];
	if (!kwMap && !modMap) return code;
	return code
		.split("\n")
		.map((line) => translateLine(line, kwMap || {}, modMap || {}))
		.join("\n");
}

/**
 * Translate locale Jadle back to English for storage/API.
 * No-op for "en" or unknown locales.
 */
export function translateJadleToEnglish(code: string, locale?: string): string {
	const loc = locale ?? getLocale();
	if (!loc || loc === "en") return code;
	const kwMap = getReverseKeywords(loc);
	const modMap = getReverseModifiers(loc);
	if (!Object.keys(kwMap).length && !Object.keys(modMap).length) return code;
	return code
		.split("\n")
		.map((line) => translateLine(line, kwMap, modMap))
		.join("\n");
}

// ── Exports for syntax highlighting / autocomplete ──────────────────

/**
 * Returns locale→English keyword map for the given locale.
 * Used by the keyword highlighter ViewPlugin.
 */
export function getLocaleKeywordMap(locale?: string): TranslationMap {
	const loc = locale ?? getLocale();
	return getReverseKeywords(loc);
}

/**
 * Returns locale→English modifier map for the given locale.
 */
export function getLocaleModifierMap(locale?: string): TranslationMap {
	const loc = locale ?? getLocale();
	return getReverseModifiers(loc);
}

/**
 * Returns English→locale keyword map.
 */
export function getEnglishToLocaleKeywordMap(locale?: string): TranslationMap {
	const loc = locale ?? getLocale();
	return keywordTranslations[loc] || {};
}

/**
 * Returns English→locale modifier map.
 */
export function getEnglishToLocaleModifierMap(locale?: string): TranslationMap {
	const loc = locale ?? getLocale();
	return modifierTranslations[loc] || {};
}
